import React from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import classnames from 'clsx';
import PropTypes from 'prop-types';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { InventoryService } from '../../../services/inventory-service';
import style from '../style.module.less';
import { emptyArray } from 'ui-components/default-props/empty';

const getInventoryStockLevelStyle = (doesInventorySourceSupportAllocation, stockLevelQuantityType, isAllocatable, isAllocated) => {
  if (!doesInventorySourceSupportAllocation || stockLevelQuantityType !== InventoryService.StockLevelQuantityType.Available) {
    return style.black;
  }
  if (!isAllocatable && !isAllocated) {
    return style.red;
  }
  if (isAllocatable && !isAllocated) {
    return style.green;
  }
  return style.black;
};

const renderInventoryStockLevelLabel = (quantityType, fallbackLabel) => {
  switch (quantityType) {
    case InventoryService.StockLevelQuantityType.Allocated:
      return <FormattedMessage defaultMessage='Allocated' description='inventoryStockCount.allocated' id='0JDytp' />;
    case InventoryService.StockLevelQuantityType.Total:
      return <FormattedMessage defaultMessage='On Hand' description='inventoryStockCount.total' id='PJuH6O' />;
    case InventoryService.StockLevelQuantityType.Available:
      return <FormattedMessage defaultMessage='Available' description='inventoryStockCount.available' id='fSRC7T' />;
    // Currently we don't have a mapping layer in our backend for Inventory Provider supplied stock levels.
    // Because of that, we need to treat those stock level "types" as purely data-driven, which means that
    // we can't use our UI-determined labels for these values.
    case InventoryService.StockLevelQuantityType.InventoryProviderSpecified:
    default:
      return fallbackLabel;
  }
};

const renderInventoryStockLevels = (inventoryStockLevels, doesInventorySourceSupportAllocation, isAllocatable, isAllocated) => {
  return (
    <div className={classnames(style['content-wrapper'], style['inventory-wrapper'])}>
      {inventoryStockLevels.map(({ label, quantity, type }, index) => {
        if (type === InventoryService.StockLevelQuantityType.Allocated) {
          return null;
        }

        return (
          <div key={index} className={getInventoryStockLevelStyle(doesInventorySourceSupportAllocation, type, isAllocatable, isAllocated)}>
            <div className={style['inventory-padding']}>{renderInventoryStockLevelLabel(type, label)}</div>
            <div className={style['inventory-padding']}>
              <FormattedNumber value={quantity} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

const renderStockInformationUnknownMessage = () => {
  return (
    <div className={classnames(style['content-wrapper'], style['unknown-message'])}>
      <FormattedMessage defaultMessage='No stock level established' description='items.inventoryUnknown' id='erbnKe' />
    </div>
  );
};

export const InventoryItemStockCount = ({
  inventoryStockLevels = emptyArray,
  doesInventorySourceSupportAllocation = false,
  shouldBeHidden = false,
  isLoading = false,
  isAllocatable = false,
  isAllocated = false,
  className = null,
  onClick = null,
}) => {
  if (shouldBeHidden) {
    return null;
  }

  if (isLoading) {
    return (
      <div className={classnames(style.tile, className)}>
        <div className={classnames(style['content-wrapper'], style['inventory-wrapper'])}>
          <LoadingIndicator className={style['loading-indicator']} />
        </div>
      </div>
    );
  }

  return (
    <div className={classnames(style.tile, className)} onClick={onClick}>
      {inventoryStockLevels.length
        ? renderInventoryStockLevels(inventoryStockLevels, doesInventorySourceSupportAllocation, isAllocatable, isAllocated)
        : renderStockInformationUnknownMessage()}
    </div>
  );
};

InventoryItemStockCount.propTypes = {
  inventoryStockLevels: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      quantity: PropTypes.number.isRequired,
      type: PropTypes.oneOf(Object.keys(InventoryService.StockLevelQuantityType)).isRequired,
    }),
  ),
  doesInventorySourceSupportAllocation: PropTypes.bool,
  shouldBeHidden: PropTypes.bool,
  isLoading: PropTypes.bool,
  isAllocatable: PropTypes.bool,
  isAllocated: PropTypes.bool,
  className: PropTypes.string,
  onClick: PropTypes.func,
};
